import type { PropsWithChildren } from 'react'
import { Children, useEffect, useMemo } from 'react'

import { type Locale, setDefaultOptions } from 'date-fns'
import { de, enAU, enCA, enGB, enUS, es, fr, nl, ptBR } from 'date-fns/locale'
import moment from 'moment'
import { RawIntlProvider } from 'react-intl'
import { useSelector } from 'react-redux'

import { type DateLocales } from '@webapp/core/components/FormattedDate/types'
import {
  DEFAULT_FULL_LOCALE,
  DEFAULT_LOCALE,
  generateIntl,
  translationMessages,
} from '../../../core/i18n'
import { makeSelectFullLocale, makeSelectLocale } from '../../../stores/i18n'

const LocaleMap: Record<DateLocales, Locale> = {
  en: enUS,
  'en-us': enUS,
  'en-au': enAU,
  'en-ca': enCA,
  'en-gb': enGB,
  fr,
  es,
  'es-us': es,
  de,
  nl,
  'pt-br': ptBR,
}

export const LanguageProvider = ({ children }: PropsWithChildren<{}>) => {
  const locale = useSelector(makeSelectLocale())
  const fullLocale = useSelector(makeSelectFullLocale())

  const intl = useMemo(
    () =>
      generateIntl({
        locale: fullLocale,
        defaultLocale: fullLocale,
        messages: translationMessages[locale],
        wrapRichTextChunksInFragment: true,
      }),
    [locale, fullLocale]
  )

  useEffect(() => {
    if (locale !== DEFAULT_LOCALE || fullLocale !== DEFAULT_FULL_LOCALE) {
      moment.locale(fullLocale || DEFAULT_LOCALE)
      setDefaultOptions({ locale: LocaleMap[fullLocale || DEFAULT_LOCALE] })
    }
  }, [locale, fullLocale])

  return (
    <RawIntlProvider value={intl}>{Children.only(children)}</RawIntlProvider>
  )
}
